import React, { useState, useEffect } from 'react';
import styles from './style.module.scss';
import { buildClassName } from 'utils/build-class-name';
import InfoIcon from 'components/icon/info-icon';

interface StatCardProps {
  /** The title to display (e.g., "Median Home Price", "Avg. Days on Market") */
  title: string;
  /** The pre-formatted value to display (e.g., "$1,250,000", "9 days", "85%") */
  formattedValue: string;
  /** The tooltip content explaining what this metric represents */
  tooltipContent: string;
  /** The time period this metric represents (e.g., "Past 30 days", "Past 90 days") */
  period: string;
  /** Custom last updated timestamp. If not provided, current timestamp will be used */
  lastUpdated?: string;
  /** If true, we will not show any data but a not available state */
  showNotAvailable?: boolean;
}

/**
 * A flexible metric display component that shows any formatted value with tooltip and metadata.
 * Can be used for prices, days, percentages, counts, or any other statistical values.
 */
const StatCard = ({
  title,
  formattedValue,
  tooltipContent,
  period,
  lastUpdated,
  showNotAvailable,
}: StatCardProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  // Use state + useEffect for the timestamp to avoid server/client hydration mismatch
  const [displayLastUpdated, setDisplayLastUpdated] = useState(lastUpdated || '');

  useEffect(() => {
    if (!lastUpdated) {
      const now = new Date();
      const dateStr = now.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }) + '. ' + now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
      // eslint-disable-next-line react-hooks/set-state-in-effect -- client-only timestamp to avoid SSR/client hydration mismatch
      setDisplayLastUpdated(dateStr);
    }
  }, [lastUpdated]);

  if (!formattedValue) {
    return null;
  }

  return (
    <div className={styles.component}>
      <div className={styles.header}>
        <span className={styles.title}>
          {title}
        </span>
        <div
          className={styles.infoIconContainer}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <InfoIcon />
          {showTooltip && (
            <div className={styles.tooltip}>
              <div className={styles.tooltipContent}>
                {tooltipContent}
              </div>
              <div className={styles.tooltipArrow}></div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.value}>
        {!showNotAvailable && formattedValue}
      </div>

      <div className={buildClassName(styles.period, showNotAvailable && styles.unavailable)}>
        {showNotAvailable ? `${title} not available` : period}
      </div>

      {!showNotAvailable && displayLastUpdated &&
        <div className={styles.lastUpdated}>
          Last Updated: {displayLastUpdated}
        </div>
      }
    </div>
  );
};

export default StatCard;
